/* sb-native-signup.js
   Native replacement for the Klaviyo async embed on the top signup overlay.
   Posts directly to Klaviyo's public client subscriptions API so the popup
   no longer depends on onsite.js reliably scanning + rendering the embedded
   form (which was intermittently failing to render at all, appearing to
   users as a frozen/blank signup box).
*/
(function () {
  const COMPANY_ID = "RTGPfF";
  const ENDPOINT = "https://a.klaviyo.com/client/subscriptions/?company_id=" + COMPANY_ID;
  const DEFAULT_LIST_ID = "XfjHAZ"; // "SOBAKER - Access"

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  }

  // Best-effort normalize to E.164. Assumes US/Canada (10-digit) numbers
  // when no country code is present, since that is this store's audience.
  function toE164(raw) {
    const trimmed = String(raw || "").trim();
    if (!trimmed) return "";
    const digits = trimmed.replace(/[^\d+]/g, "");
    if (digits.startsWith("+")) return digits;
    if (digits.length === 10) return "+1" + digits;
    if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
    return digits ? "+" + digits : "";
  }

  function setError(root, message) {
    const errorEl = root.querySelector("[data-sb-signup-error]");
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = "";
      errorEl.hidden = true;
    }
  }

  function setSubmitting(root, isSubmitting) {
    const btn = root.querySelector("[data-sb-signup-submit]");
    const label = root.querySelector("[data-sb-signup-submit-label]");
    if (!btn) return;
    btn.disabled = isSubmitting;
    if (label) label.textContent = isSubmitting ? "..." : "UNLOCK";
  }

  async function submitProfile(listId, email, phone) {
    const subscriptions = {};
    if (email) subscriptions.email = { marketing: { consent: "SUBSCRIBED" } };
    if (phone) subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };

    const profileAttributes = {
      subscriptions: subscriptions,
      properties: { $consent_method: "SOBAKER native signup form" },
    };
    if (email) profileAttributes.email = email;
    if (phone) profileAttributes.phone_number = phone;

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: { data: { type: "profile", attributes: profileAttributes } },
        },
        relationships: {
          list: { data: { type: "list", id: listId } },
        },
      },
    };

    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        revision: "2025-01-15",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok && res.status !== 202) {
      let detail = "";
      try {
        detail = await res.text();
      } catch (_) {}
      throw new Error("Klaviyo subscribe failed (" + res.status + "): " + detail);
    }
  }

  function initForm(root) {
    const form = root.querySelector("[data-sb-signup-form]");
    if (!form || form.dataset.sbSignupInit === "true") return;
    form.dataset.sbSignupInit = "true";

    const listId = root.getAttribute("data-list-id") || DEFAULT_LIST_ID;
    const successEl = root.querySelector("[data-sb-signup-success]");

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      setError(root, "");

      const emailInput = form.querySelector('input[name="email"]');
      const phoneInput = form.querySelector('input[name="phone"]');
      const emailRaw = emailInput ? emailInput.value.trim() : "";
      const phoneRaw = phoneInput ? phoneInput.value.trim() : "";

      // Either field alone is enough. Only validate a field's format if the
      // person actually typed something into it.
      if (!emailRaw && !phoneRaw) {
        setError(root, "Enter an email or phone number.");
        if (emailInput) {
          try {
            emailInput.focus();
          } catch (_) {}
        }
        return;
      }

      if (emailRaw && !isValidEmail(emailRaw)) {
        setError(root, "Enter a valid email address.");
        if (emailInput) {
          try {
            emailInput.focus();
          } catch (_) {}
        }
        return;
      }

      const phone = phoneRaw ? toE164(phoneRaw) : "";
      if (phoneRaw && !phone) {
        setError(root, "Enter a valid phone number.");
        if (phoneInput) {
          try {
            phoneInput.focus();
          } catch (_) {}
        }
        return;
      }

      const email = emailRaw;

      setSubmitting(root, true);

      submitProfile(listId, email, phone)
        .then(function () {
          form.hidden = true;
          if (successEl) successEl.hidden = false;
        })
        .catch(function (err) {
          console.error("[sb-native-signup] submit failed:", err);
          setError(root, "Something went wrong. Please try again in a moment.");
        })
        .finally(function () {
          setSubmitting(root, false);
        });
    });
  }

  // Keep the "EXCLUSIVE ACCESS & EARLY UPDATES" heading on a single line on
  // every device by shrinking its font-size until the text actually fits
  // the space available next to the close button, instead of trusting any
  // one fixed font-size/vw guess to work across all screen widths/fonts.
  const MAX_HEADING_FONT = 18;
  const MIN_HEADING_FONT = 9;

  function fitHeading(h1) {
    if (!h1) return;
    const span = h1.querySelector("span") || h1;
    const cs = getComputedStyle(h1);
    const paddingLeft = parseFloat(cs.paddingLeft) || 0;
    const paddingRight = parseFloat(cs.paddingRight) || 0;
    const availableWidth = h1.clientWidth - paddingLeft - paddingRight;
    if (availableWidth <= 0) return;

    let fontSize = MAX_HEADING_FONT;
    h1.style.fontSize = fontSize + "px";

    // Use getBoundingClientRect (not scrollWidth, which is unreliable/0 on
    // plain inline elements) to read the text's true rendered width even
    // when it overflows its box, which is exactly what we need to check.
    let guard = 40;
    while (fontSize > MIN_HEADING_FONT && span.getBoundingClientRect().width > availableWidth && guard > 0) {
      fontSize -= 0.5;
      h1.style.fontSize = fontSize + "px";
      guard -= 1;
    }
  }

  function fitAllHeadings() {
    document
      .querySelectorAll(".sb-native-signup-form__heading h1")
      .forEach(fitHeading);
  }

  function initHeadingFit() {
    fitAllHeadings();
    document.addEventListener("sb:klaviyo-overlay:open", fitAllHeadings);

    let resizeTimer = 0;
    window.addEventListener("resize", function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(fitAllHeadings, 120);
    });
    window.addEventListener("orientationchange", function () {
      window.setTimeout(fitAllHeadings, 200);
    });
  }

  function init() {
    document.querySelectorAll("[data-sb-native-signup]").forEach(initForm);
    initHeadingFit();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();


